import { memo, useState, useCallback } from 'react';
import { Typography, Stack, Badge, Box } from '@mui/material';
import { format } from 'date-fns';

import { CloseIcon } from '@/assets/icons';
import { theme } from '@/modules/common/theme';

import { NotificationType, type BaseNotification as NotificationInterface } from '../helpers/types';
import { useNotificationStore } from '../store/useNotificationStore';
import { getSeverityStyles } from './SeverityStyles';
import NotificationSeverityActions from './NotificationSeverityActions';
import NotificationIcon from './NotificationIcon';

interface NotificationProps {
  notification: NotificationInterface;
}

function Notification({ notification }: NotificationProps) {
  const { removeNotification } = useNotificationStore();
  const [isHovered, setIsHovered] = useState(false);
  const handleRemove = useCallback(
    () => removeNotification(notification.id),
    [removeNotification, notification.id],
  );

  const notificationTime = format(new Date(notification.timestamp), 'HH:mm');

  return (
    <Stack
      className='notification'
      spacing={2}
      py={2}
      sx={{
        width: '100%',
        backgroundColor: { ...getSeverityStyles(notification.severity) },
        transition: 'background-color 0.1s ease',
      }}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => notification.onClick && notification.onClick()}
    >
      <Stack
        direction='row'
        spacing={2}
        px={2}
        sx={{
          justifyContent: 'left',
          alignItems: 'left',
        }}
      >
        <Stack sx={{ color: theme.palette.others.black, top: 14 }}>
          <NotificationIcon type={NotificationType[notification.severity]} />
        </Stack>
        <Stack>
          <Typography
            sx={{
              fontWeight: 600,
              color: 'neutral.darker',
              justifyContent: 'left',
            }}
          >
            {notification.title}
          </Typography>
        </Stack>
        <Stack
          direction='row'
          sx={{
            cursor: 'pointer',
            width: '40%',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          {isHovered ? (
            <CloseIcon onClick={handleRemove} />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography fontSize={10} color='neutral.main'>
                {notificationTime}
              </Typography>
              {!notification.read && (
                <Badge color='primary' variant='dot' sx={{ pl: 1.5, ml: 1 }} />
              )}
            </Box>
          )}
        </Stack>
      </Stack>
      <Stack px={2} spacing={2}>
        <Typography lineHeight='16px' fontSize={10} sx={{ wordBreak: 'break-word' }}>
          {notification.description}
          {notification.solution ? ` ${notification.solution}` : ''}
        </Typography>
      </Stack>
      {NotificationSeverityActions({ notification })}
    </Stack>
  );
}

export default memo(Notification);
